import React, {
  cloneElement,
  type ErrorInfo,
  type ReactNode,
  type JSX,
} from "react";

import { composition } from "../../features/utils/composition";

type ErrorBoundaryFallbackProps = {
  /**
   * @internal - The error that was thrown.
   */
  _error?: Error;
  children: ReactNode | ((error: Error) => JSX.Element);
};

export function ErrorBoundaryFallback({
  _error: error,
  children,
}: ErrorBoundaryFallbackProps): JSX.Element {
  if (!error) {
    throw new Error(
      "ErrorBoundaryFallback should not be rendered without an error",
    );
  }

  return <>{typeof children === "function" ? children(error) : children}</>;
}

type ErrorBoundaryRootProps = {
  children: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
};

type ErrorBoundaryRootState = {
  error?: Error;
};

export class ErrorBoundaryRoot extends React.Component<
  ErrorBoundaryRootProps,
  ErrorBoundaryRootState
> {
  public state: ErrorBoundaryRootState = {};

  static getDerivedStateFromError(error: Error): ErrorBoundaryRootState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  render(): JSX.Element {
    const { fallback, rest } = composition.split(this.props.children, {
      fallback: composition.single(ErrorBoundaryFallback).required(),
    });
    if (this.state.error) {
      return cloneElement(fallback, {
        _error: this.state.error,
      });
    }

    return <>{rest}</>;
  }
}
